import 'twin.macro';

import { Card, Image, Stack, Text } from '@cheqroom/ui';
import { FC, useMemo } from 'react';

import itemPreviewLogo from '../../../../public/assets/images/item-preview.png';
import { getIconForField } from '../../../hooks/item-fields/helper';
import { CustomField, SystemField, SystemFieldType, VirtualFieldType } from '../../../hooks/item-fields/types';
import type { InlineField } from '../../Overview/TableView/components/Cells/NameCell/InlineFields';
import InlineFields from '../../Overview/TableView/components/Cells/NameCell/InlineFields';
import getDefaultValueForItemField from '../helpers/get-default-value-for-item-field';
import { CardTag } from './ItemPreview.styles';

type Props = {
	viewType: 'table' | 'cards' | 'scheduler';
	fieldIds: string[];
	getFieldById: (fieldId: string) => SystemField<SystemFieldType> | CustomField | undefined;
};

/**
 * Transforms fieldIds into InlineField[] with proper preview data structure.
 * Handles location and sublocation fields with mock data, other fields use default values.
 */
const usePreviewFields = (
	fieldIds: string[],
	getFieldById: (fieldId: string) => SystemField<SystemFieldType> | CustomField | undefined
): InlineField[] => {
	return useMemo(() => {
		return fieldIds.map((fieldId) => {
			const field = getFieldById(fieldId);
			const defaultValue = getDefaultValueForItemField(fieldId);

			// Handle location field - create proper LocationData structure
			if (fieldId === SystemFieldType.location) {
				return {
					id: fieldId,
					value: {
						_id: 'preview-location-id',
						name: defaultValue ?? 'Cheqroom HQ',
						icon: 'Marker' as const,
					},
					icon: getIconForField(fieldId),
				};
			}

			// Handle sublocation field - create proper SublocationData structure
			// Root location is first element, sublocations follow
			if (fieldId === VirtualFieldType.sublocation) {
				return {
					id: fieldId,
					value: [
						{
							_id: 'preview-root-location-id',
							name: 'Cheqroom HQ',
							icon: 'Marker' as const,
						},
						{
							_id: 'preview-sublocation-id',
							name: defaultValue ?? 'Room 101',
							icon: 'Building' as const,
						},
					],
					icon: getIconForField(fieldId),
				};
			}

			// For other fields, use the default value or field name
			// Note: SystemField and CustomField don't have editor/kind properties
			// Formatting will be handled by InlineFields based on fieldId patterns
			return {
				id: fieldId,
				value: defaultValue ?? field?.name ?? '',
				icon: getIconForField(fieldId),
			};
		});
	}, [fieldIds, getFieldById]);
};

const RowPreview: FC<Omit<Props, 'viewType'>> = ({ fieldIds, getFieldById }) => {
	const fields = usePreviewFields(fieldIds, getFieldById);

	return (
		<Stack vertical tw="w-full">
			<CardTag size="small">Preview</CardTag>
			<Card tw="px-4 py-3 relative">
				<Stack spacing="tight" tw="overflow-hidden">
					<Image
						// TODO localise this alt text
						alt="item preview"
						fit="contain"
						src={itemPreviewLogo}
						tw="h-[40px] w-[40px]"
					/>
					<Stack vertical tw="overflow-hidden">
						<Text weight="bold">GoPro Hero 10 Black</Text>
						<InlineFields fields={fields} />
					</Stack>
				</Stack>
			</Card>
		</Stack>
	);
};

const CardPreview: FC<Omit<Props, 'viewType'>> = ({ fieldIds, getFieldById }) => {
	const fields = usePreviewFields(fieldIds, getFieldById);

	return (
		<Stack vertical tw="w-80">
			<CardTag size="small">Preview</CardTag>
			<Card tw="relative">
				<Image src={itemPreviewLogo} alt="Item preview image" tw="h-52 mt-6" fit="contain" />
				<Card.Footer>
					<Stack vertical spacing="tight">
						<Text weight="bold">GoPro Hero 10 Black</Text>
						<InlineFields fields={fields} />
					</Stack>
				</Card.Footer>
			</Card>
		</Stack>
	);
};

const ItemPreview: FC<Props> = ({ viewType, ...props }) => {
	switch (viewType) {
		case 'scheduler':
		case 'table':
			return <RowPreview {...props} />;
		case 'cards':
			return <CardPreview {...props} />;
		default:
			return null;
	}
};

export default ItemPreview;
